/**
 * Copy Block V2 Mappings
 *
 * Mappings for CopyBlock elements (title, subtitle, subtitle2).
 * Targets: content.copy.title.appearance.*, content.copy.subtitle.appearance.*
 *
 * Applies to all StepKinds that have a CopyBlock:
 * HeroContent, ListMultiPick, ListSinglePick, GridMultiPick, GridSinglePick,
 * ProgressSteps, TextInput, WheelPicker, HeroList, Paywall
 *
 * @module themes/mappers/mappingTablesV2
 */

import type { ThemeV2Mapping } from '../ThemeV2Mapper';
import type { StepKind } from '@segmently/schema-models/v2';

// ============================================================================
// Transform Helpers
// ============================================================================

/**
 * Add 50% alpha to hex color for subtitle colors.
 * Used as transform in mappings to apply 50% transparency.
 */
export const addAlpha50 = (color: unknown): unknown => {
  if (typeof color === 'string' && color.length === 7 && color.startsWith('#')) {
    return `${color}80`; // 80 = ~50% in hex (128/255)
  }
  return color;
};

// ============================================================================
// StepKind Helpers
// ============================================================================

/** StepKinds that have a copy block */
const hasCopy = (stepKind: StepKind): boolean =>
  stepKind !== 'WebEmbed' &&
  stepKind !== 'Carousel' &&
  stepKind !== 'FlexibleLayout';

// ============================================================================
// Copy Block Mappings
// ============================================================================

/**
 * Copy block font/color mappings.
 *
 * V2 paths:
 * - Title color: copy.title.appearance.color (100% titlesSubtitles)
 * - Subtitle color: copy.subtitle.appearance.color (50% titlesSubtitles)
 * - Subtitle2 color: copy.subtitle2.appearance.color (50% titlesSubtitles)
 * - Title font properties: copy.title.appearance.fontFamily/fontSize/fontWeight/align
 * - Subtitle font properties: copy.subtitle.appearance.fontFamily/fontSize/fontWeight/align
 */
export const COPY_BLOCK_V2_MAPPINGS: ThemeV2Mapping[] = [
  // === Title Color ===
  {
    source: 'colors.titlesSubtitles',
    target: 'copy.title.appearance.color',
    condition: hasCopy,
  },

  // === Subtitle Color (50% alpha) ===
  {
    source: 'colors.titlesSubtitles',
    target: 'copy.subtitle.appearance.color',
    transform: addAlpha50,
    condition: hasCopy,
  },

  // === Subtitle2 Color (50% alpha) ===
  {
    source: 'colors.titlesSubtitles',
    target: 'copy.subtitle2.appearance.color',
    transform: addAlpha50,
    condition: hasCopy,
  },

  // === Title Font Properties ===
  {
    source: 'fonts.title.fontFamily',
    target: 'copy.title.appearance.fontFamily',
    condition: hasCopy,
  },
  {
    source: 'fonts.title.fontSize',
    target: 'copy.title.appearance.fontSize',
    condition: hasCopy,
  },
  {
    source: 'fonts.title.fontWeight',
    target: 'copy.title.appearance.fontWeight',
    condition: hasCopy,
  },
  {
    source: 'fonts.title.align',
    target: 'copy.title.appearance.align',
    condition: hasCopy,
  },

  // === Subtitle Font Properties ===
  {
    source: 'fonts.subtitle.fontFamily',
    target: 'copy.subtitle.appearance.fontFamily',
    condition: hasCopy,
  },
  {
    source: 'fonts.subtitle.fontSize',
    target: 'copy.subtitle.appearance.fontSize',
    condition: hasCopy,
  },
  {
    source: 'fonts.subtitle.fontWeight',
    target: 'copy.subtitle.appearance.fontWeight',
    condition: hasCopy,
  },
  {
    source: 'fonts.subtitle.align',
    target: 'copy.subtitle.appearance.align',
    condition: hasCopy,
  },

  // === Granular colors (last-defined override over the master palette) ===
  // ThemeV2EditorView materializes these from colors.titlesSubtitles for the
  // broad fallback. Direct edits intentionally keep their exact authored
  // value; subtitle fields must not receive another alpha transform here.
  {
    source: 'fonts.title.color',
    target: 'copy.title.appearance.color',
    condition: hasCopy,
  },
  {
    source: 'fonts.subtitle.color',
    target: 'copy.subtitle.appearance.color',
    condition: hasCopy,
  },
  {
    source: 'fonts.subtitle.color',
    target: 'copy.subtitle2.appearance.color',
    condition: hasCopy,
  },

  // === Main fontFamily cascade (overrides title/subtitle if set) ===
  {
    source: 'fonts.main.fontFamily',
    target: 'copy.title.appearance.fontFamily',
    condition: hasCopy,
  },
  {
    source: 'fonts.main.fontFamily',
    target: 'copy.subtitle.appearance.fontFamily',
    condition: hasCopy,
  },
  {
    source: 'fonts.main.fontFamily',
    target: 'copy.subtitle2.appearance.fontFamily',
    condition: hasCopy,
  },
];
